import { useMemo } from 'react';
import {
  CONSUMER_PAYMENT_SCHEDULES,
  consumerQuoteKey,
  projectConsumerQuote,
  type ConsumerQuote,
  type ConsumerQuoteProjection,
} from '@shared/consumerPlan';
import { useConsumerClient } from './use-consumer-client';
import { useConsumerPlan } from './use-consumer-plan';
import { useConsumerQuotes } from './use-consumer-quotes';

function useProjections(client: Client | null, quotes: ConsumerQuote[]) {
  return useMemo(
    () =>
      new Map<string, ConsumerQuoteProjection>(
        client ? quotes.map(quote => [consumerQuoteKey(quote), projectConsumerQuote(client, quote)]) : [],
      ),
    [client, quotes],
  );
}

/**
 * The three coverage options for the chosen payment schedule and the selected one, each
 * projected month by month against the consumer's care plan.
 */
export function useCoverageSelection(clientId: string) {
  const { client, scenario } = useConsumerClient(clientId);
  const { plan, updatePlan } = useConsumerPlan(clientId);
  const quotesQuery = useConsumerQuotes(clientId);
  const { quotes, coverageOptions } = quotesQuery;
  const optionsBySchedule = useMemo(
    () => new Map(CONSUMER_PAYMENT_SCHEDULES.map(lane => [lane, coverageOptions[lane]] as const)),
    [coverageOptions],
  );
  // A saved schedule with no quotes falls back to 10-pay, then to whichever schedule has quotes.
  const preferred = plan?.paymentSchedule ?? 'tenPay';
  const schedule =
    [preferred, 'tenPay' as const, ...CONSUMER_PAYMENT_SCHEDULES].find(
      lane => (optionsBySchedule.get(lane)?.length ?? 0) > 0,
    ) ?? preferred;
  const options = useMemo(() => optionsBySchedule.get(schedule) ?? [], [optionsBySchedule, schedule]);

  const selectedQuote = useMemo(
    () => quotes.find(quote => consumerQuoteKey(quote) === plan?.selectedQuoteKey) ?? null,
    [quotes, plan?.selectedQuoteKey],
  );

  const projectedQuotes = useMemo(
    () => [
      ...options.map(option => option.quote),
      ...(selectedQuote ? [selectedQuote] : []),
    ],
    [options, selectedQuote],
  );
  const projections = useProjections(client, projectedQuotes);
  const selectedProjection = selectedQuote
    ? (projections.get(consumerQuoteKey(selectedQuote)) ?? null)
    : null;

  return {
    ...quotesQuery,
    client,
    scenario,
    plan,
    updatePlan,
    schedule,
    options,
    optionsBySchedule,
    projections,
    selectedQuote,
    selectedProjection,
  };
}
